import React from "react";
import { useInView } from "../../hooks/useInView";

// Multi-line chart. Each series is indexed to 100 at its first value so
// different-scale metrics (searches vs. returns) can share one axis —
// exactly how the source artifact draws it.
export default function AnimatedLineChart({ series, labels, height = 190 }) {
  const { ref, inView } = useInView();

  const W = 660;
  const H = height;
  const padL = 6;
  const padR = 64;
  const padT = 18;
  const padB = 30;

  const indexed = series.map((s) => {
    const base = s.values[0] || 1;
    return { ...s, idx: s.values.map((v) => (v / base) * 100) };
  });

  const all = indexed.flatMap((s) => s.idx).concat([100]);
  let min = Math.min(...all);
  let max = Math.max(...all);
  const pad = (max - min) * 0.18 || 12;
  min -= pad;
  max += pad;

  const n = labels.length;
  const x = (i) => padL + (i / (n - 1)) * (W - padL - padR);
  const y = (v) => padT + (1 - (v - min) / (max - min)) * (H - padT - padB);

  const linePath = (idx) =>
    idx.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");

  const areaPath = (idx) =>
    `${linePath(idx)} L${x(n - 1).toFixed(1)},${y(min).toFixed(1)} L${x(0).toFixed(1)},${y(min).toFixed(1)} Z`;

  const gridVals = [min, (min + max) / 2, max];

  return (
    <div ref={ref} className="chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" preserveAspectRatio="none">
        <defs>
          {indexed.map((s) => (
            <linearGradient key={s.key} id={`grad-${s.key}-${Math.round(y(s.idx[0]))}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity="0.14" />
              <stop offset="100%" stopColor={s.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>

        {gridVals.map((g, i) => (
          <line key={i} x1={padL} x2={W - padR} y1={y(g)} y2={y(g)} className="chart-grid" />
        ))}
        {/* baseline 100 */}
        <line x1={padL} x2={W - padR} y1={y(100)} y2={y(100)} className="chart-baseline" />
        <text x={padL} y={y(100) - 5} className="chart-axis-num">100</text>

        {indexed.map((s, si) => (
          <path
            key={`a-${s.key}`}
            d={areaPath(s.idx)}
            fill={`url(#grad-${s.key}-${Math.round(y(s.idx[0]))})`}
            className={`chart-area ${inView ? "in" : ""}`}
            style={{ transitionDelay: `${0.5 + si * 0.15}s` }}
          />
        ))}

        {indexed.map((s, si) => (
          <path
            key={`l-${s.key}`}
            d={linePath(s.idx)}
            fill="none"
            stroke={s.color}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength="1"
            className={`chart-line ${inView ? "in" : ""}`}
            style={{ transitionDelay: `${si * 0.18}s` }}
          />
        ))}

        {indexed.map((s, si) => {
          const last = s.idx[s.idx.length - 1];
          return (
            <g key={`d-${s.key}`} className={`chart-dot ${inView ? "in" : ""}`} style={{ transitionDelay: `${1 + si * 0.15}s` }}>
              <circle cx={x(n - 1)} cy={y(last)} r="7" fill={s.color} opacity="0.18" className="chart-pulse" />
              <circle cx={x(n - 1)} cy={y(last)} r="3.4" fill={s.color} />
              <text x={x(n - 1) + 9} y={y(last) + 4} className="chart-endval" fill={s.color}>
                {Math.round(last)}
              </text>
            </g>
          );
        })}
      </svg>

      <div className="chart-xaxis">
        <span>{labels[0]}</span>
        <span>{labels[labels.length - 1]}</span>
      </div>

      <div className="chart-legend">
        {series.map((s) => (
          <span key={s.key} className="legend-chip">
            <span className="legend-dot" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}
